/**
 * Список выданных диспетчеру карточек на сегодня.
 *
 * Раздачу делает сервер (§6.3, pull-режима нет): здесь только показ того,
 * что уже назначено, и переход к карточке. Кнопки «взять ещё» тут не будет.
 *
 * Компактный список, а не таблица: колонка живёт справа от звонка, и пять
 * табличных столбцов там нечитаемы. Строка отвечает на два вопроса —
 * «кому ещё звонить» и «что с этой карточкой».
 *
 * Перезвон по договорённости виден прямо в строке: «перезвон 15:00», а когда
 * время пришло — «Пора» оранжевым и «Просрочен» красным (06.09.2026). Раньше
 * напоминание жило только в блоке «Перезвоны» и гасло в момент выдачи карточки.
 */

import type { DeskAssignment } from '@cc/shared';

import { formatPhone } from '../../shared/lib/format-phone.js';
import { localTimeOf, outsideCallWindow } from '../../shared/lib/local-time.js';
import { formatTimeMsk, formatWhenMsk } from '../../shared/lib/msk.js';
import { cardDueState, type DueState } from './callback-alarm.js';
import { useNow } from './use-now.js';

export interface MyQueueProps {
  assignments: DeskAssignment[];
  /**
   * Карточки, где разговор был, а метки нет (28.09.2026): стоят наверху списка
   * с плашкой. После F5 Катя не нашла такую карточку среди 40 остальных.
   */
  needsLabelIds: ReadonlySet<string>;
  activeId: string | null;
  onSelect: (assignment: DeskAssignment) => void;
}

const STATUS_TITLES: Record<DeskAssignment['status'], string> = {
  open: 'Не звонил',
  called: 'Без метки',
  needs_label: 'Нужна метка',
  labeled: 'Закрыт',
  expired: 'Просрочен',
  revoked: 'Отозван',
};

/** Незакрытый разговор — янтарный: это единственное, что здесь требует действий */
function statusClasses(status: DeskAssignment['status']): string {
  if (status === 'needs_label' || status === 'called') return 'bg-amber-50 text-amber-700';
  return 'bg-slate-100 text-slate-600';
}

/** Фон строки: наступивший перезвон перекрывает подсветку активной карточки */
function rowBackground(due: DueState | null, isActive: boolean): string {
  if (due === 'overdue') return 'bg-red-50';
  if (due === 'due') return 'bg-amber-50';
  return isActive ? 'bg-blue-50/50' : '';
}

function dueBadgeClasses(due: DueState): string {
  if (due === 'overdue') return 'bg-red-100 text-red-800';
  if (due === 'due') return 'bg-amber-100 text-amber-800';
  return 'bg-blue-50 text-blue-700';
}

function dueBadgeText(due: DueState, at: string, now: Date): string {
  const when = formatWhenMsk(at, now);
  if (due === 'overdue') return `Просрочен: ${when}`;
  if (due === 'due') return `Пора: ${when}`;
  return `перезвон ${when}`;
}

export function MyQueue({ assignments, needsLabelIds, activeId, onSelect }: MyQueueProps): React.JSX.Element {
  const now = useNow(30_000);
  return (
    <section className="flex flex-col rounded-2xl border border-slate-200 bg-white">
      <header className="flex items-baseline justify-between px-4 pt-4 pb-2">
        <h2 className="text-base font-bold text-slate-900">Мои контакты</h2>
        <span className="text-sm tabular-nums text-slate-500">{assignments.length}</span>
      </header>

      {assignments.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-slate-500">
          Контактов пока нет. Если это надолго — проверьте линию и кнопку «Готов».
        </p>
      ) : (
        <ul className="flex max-h-[520px] flex-col overflow-y-auto pb-2">
          {assignments.map((row) => {
            // Truthy-проверка lastResult, а не !== null: в окне деплоя «новый
            // web + старый api» поля нет (см. contact-card.tsx)
            const nextAt = row.lastResult ? row.lastResult.nextActionAt : null;
            const due = cardDueState(nextAt, now);
            const isActive = row.id === activeId;
            const needsLabel = needsLabelIds.has(row.id);
            return (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(row);
                  }}
                  className={
                    'flex w-full flex-col gap-0.5 border-l-2 px-4 py-2.5 text-left hover:bg-slate-50 ' +
                    (isActive ? 'border-blue-600 ' : 'border-transparent ') +
                    (needsLabel ? 'bg-amber-50' : rowBackground(due, isActive))
                  }
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <span
                      className={
                        'tabular-nums ' +
                        (isActive ? 'font-bold text-slate-900' : 'font-medium text-slate-800')
                      }
                    >
                      {formatPhone(row.phone)}
                    </span>
                    <span className="text-xs tabular-nums text-slate-400">
                      {formatTimeMsk(row.assignedAt)}
                    </span>
                  </span>
                  {/* Служебная строка переносится по словам, а не рвётся внутри бейджа:
                      в узкой колонке «Звонил, метки нет» ломался на три строки (04.09.2026) */}
                  {needsLabel ? (
                    <span className="self-start rounded-md bg-amber-200 px-1.5 py-0.5 text-xs font-semibold text-amber-900">
                      Был разговор — поставьте метку
                    </span>
                  ) : null}
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                    {nextAt !== null && due !== null ? (
                      <span
                        title="Договорились перезвонить в это время"
                        className={`rounded-md px-1.5 py-0.5 font-semibold whitespace-nowrap ${dueBadgeClasses(due)}`}
                      >
                        {dueBadgeText(due, nextAt, now)}
                      </span>
                    ) : null}
                    <span className={`rounded-md px-1.5 py-0.5 font-medium whitespace-nowrap ${statusClasses(row.status)}`}>
                      {STATUS_TITLES[row.status]}
                    </span>
                    {/* Местное время абонента — прямо в строке: выбирать, кому
                        звонить сейчас, а кого оставить на утро, надо без
                        открытия карточки (Екатерина, 31.08.2026). Красным —
                        время вне окна звонка 9–19 */}
                    <span
                      title="Сколько сейчас времени у абонента"
                      className={
                        'font-medium tabular-nums whitespace-nowrap ' +
                        (outsideCallWindow(row.tzOffsetMin) ? 'text-red-600' : 'text-slate-600')
                      }
                    >
                      у него {localTimeOf(row.tzOffsetMin)}
                    </span>
                    <span className="whitespace-nowrap text-slate-500">
                      круг {row.roundNumber}
                      {row.roundNumber > 1 ? ' · НДЗ' : ''}
                    </span>
                  </span>
                  <span className="block min-w-0 truncate text-xs text-slate-400">{row.sourceTitle}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
